import { normalizarBusca } from '@/lib/texto';
import {
  type AtualizarEmpresaRequest,
  type CartaoCnpj,
  type Cnae,
  type EmpresaDetalhe,
  type Endereco,
  type InscricaoEstadual,
  type RegimeVigencia,
} from '@fiscoai/contracts';

type CampoGeral = 'razaoSocial' | 'nomeFantasia' | 'inicioAtividades' | 'telefone' | 'crt';
type CampoEndereco = Exclude<keyof Endereco, never>;
export type ChaveDiferenca = CampoGeral | `endereco.${CampoEndereco}`;

export interface DiferencaCampo {
  chave: ChaveDiferenca;
  rotulo: string;
  atual: string | null;
  receita: string | null;
}

export interface DiferencasCartao {
  campos: DiferencaCampo[];
  cnaes: { atual: readonly Cnae[]; receita: readonly Cnae[] } | null;
  regimes: {
    atual: readonly RegimeVigencia[];
    receita: readonly RegimeVigencia[];
    /** A fonte não informa a ECF: sugestão parcial (só Simples/MEI), nasce desmarcada. */
    incompleto: boolean;
  } | null;
  /** IEs da Receita em UFs ainda sem IE no cadastro: só acrescenta, nunca apaga. */
  inscricoes: { atual: readonly InscricaoEstadual[]; novas: readonly InscricaoEstadual[] } | null;
}

const GERAIS: readonly [CampoGeral, string][] = [
  ['razaoSocial', 'Razão social'],
  ['nomeFantasia', 'Nome fantasia'],
  ['inicioAtividades', 'Início das atividades'],
  ['telefone', 'Telefone'],
  ['crt', 'CRT'],
];

const DO_ENDERECO: readonly [CampoEndereco, string][] = [
  ['cep', 'CEP'],
  ['logradouro', 'Logradouro'],
  ['numero', 'Número'],
  ['complemento', 'Complemento'],
  ['bairro', 'Bairro'],
  ['uf', 'Estado'],
  ['municipio', 'Cidade'],
  ['codigoMunicipio', 'Código IBGE'],
];

const SO_DIGITOS = new Set<string>(['telefone', 'endereco.cep']);

/** Compara sem caixa, acento e espaços repetidos; CEP e telefone só pelos dígitos. */
function comparavel(chave: string, valor: string | null): string {
  if (!valor) return '';
  if (SO_DIGITOS.has(chave)) return valor.replace(/\D/g, '');
  return normalizarBusca(valor).replace(/\s+/g, ' ').trim();
}

function valorDaEmpresa(empresa: EmpresaDetalhe, chave: ChaveDiferenca): string | null {
  if (chave.startsWith('endereco.')) {
    const campo = chave.slice('endereco.'.length) as CampoEndereco;
    return empresa.endereco?.[campo] ?? null;
  }
  return empresa[chave as CampoGeral];
}

function valorDoCartao(cartao: CartaoCnpj, chave: ChaveDiferenca): string | null {
  if (chave.startsWith('endereco.')) {
    const campo = chave.slice('endereco.'.length) as CampoEndereco;
    return cartao.endereco?.[campo] ?? null;
  }
  // O CRT não vem da Receita: é deduzido do regime vigente sugerido (crtDoRegime).
  if (chave === 'crt') return cartao.crtSugerido;
  return cartao[chave as Exclude<CampoGeral, 'crt'>];
}

const assinaturaCnaes = (cnaes: readonly Cnae[]) =>
  cnaes
    .map((c) => `${c.codigo}${c.principal ? '*' : ''}`)
    .sort()
    .join(',');

const assinaturaRegimes = (regimes: readonly RegimeVigencia[]) =>
  regimes
    .map((r) => `${r.regime}:${r.inicio}:${r.fim ?? ''}`)
    .sort()
    .join(',');

/** IEs da Receita em UFs que ainda não têm IE no cadastro. */
export function inscricoesNovas(empresa: EmpresaDetalhe, cartao: CartaoCnpj) {
  const ufs = new Set(empresa.inscricoesEstaduais.map((i) => i.uf));
  return cartao.inscricoesEstaduais.filter((i) => !ufs.has(i.uf));
}

function diferencaInscricoes(empresa: EmpresaDetalhe, cartao: CartaoCnpj) {
  const novas = inscricoesNovas(empresa, cartao);
  return novas.length > 0 ? { atual: empresa.inscricoesEstaduais, novas } : null;
}

/** O que o cartão CNPJ traz de diferente do cadastro — só o que difere. */
export function diferencasDoCartao(empresa: EmpresaDetalhe, cartao: CartaoCnpj): DiferencasCartao {
  const candidatos: [ChaveDiferenca, string][] = [
    ...GERAIS,
    ...(cartao.endereco
      ? DO_ENDERECO.map(([c, r]): [ChaveDiferenca, string] => [`endereco.${c}`, r])
      : []),
  ];
  const campos = candidatos
    .map(([chave, rotulo]) => ({
      chave,
      rotulo,
      atual: valorDaEmpresa(empresa, chave),
      receita: valorDoCartao(cartao, chave),
    }))
    .filter(
      (d) => d.receita !== null && comparavel(d.chave, d.atual) !== comparavel(d.chave, d.receita),
    );
  const cnaesDiferem =
    cartao.cnaes.length > 0 && assinaturaCnaes(empresa.cnaes) !== assinaturaCnaes(cartao.cnaes);
  // Sem a ECF a sugestão é parcial: nunca substitui um regime já cadastrado (produção, 14/10/2026).
  const parcialSobreCadastro = !cartao.ecfInformada && empresa.regimes.length > 0;
  const regimesDiferem =
    !parcialSobreCadastro &&
    cartao.regimesSugeridos.length > 0 &&
    assinaturaRegimes(empresa.regimes) !== assinaturaRegimes(cartao.regimesSugeridos);
  return {
    campos,
    cnaes: cnaesDiferem ? { atual: empresa.cnaes, receita: cartao.cnaes } : null,
    regimes: regimesDiferem
      ? {
          atual: empresa.regimes,
          receita: cartao.regimesSugeridos,
          incompleto: !cartao.ecfInformada,
        }
      : null,
    inscricoes: diferencaInscricoes(empresa, cartao),
  };
}

/** PATCH com as linhas escolhidas; o endereço parte do atual (ou do cartão, se não houver). */
export function aplicarDiferencas(
  empresa: EmpresaDetalhe,
  cartao: CartaoCnpj,
  escolhidas: readonly ChaveDiferenca[],
): Partial<AtualizarEmpresaRequest> {
  const patch: Partial<AtualizarEmpresaRequest> = {};
  for (const chave of escolhidas) {
    if (!chave.startsWith('endereco.')) {
      const valor = valorDoCartao(cartao, chave);
      Object.assign(patch, { [chave]: valor });
    }
  }
  const camposEndereco = escolhidas
    .filter((c) => c.startsWith('endereco.'))
    .map((c) => c.slice('endereco.'.length) as CampoEndereco);
  const doCartao = cartao.endereco;
  const base: Endereco | null = empresa.endereco ?? doCartao;
  if (doCartao && base && camposEndereco.length > 0) {
    const novo: Endereco = { ...base };
    for (const campo of camposEndereco) Object.assign(novo, { [campo]: doCartao[campo] });
    patch.endereco = novo;
  }
  return patch;
}
